/**
 * Business Onboarding Validation Utilities
 * Frontend validation schemas and helpers
 */

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
  return emailRegex.test(email);
};

// Phone validation
export const validatePhone = (phone) => {
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
  return phoneRegex.test(phone);
};

// URL validation
export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Step 1: Business Information Validation
export const validateBusinessInfo = (data) => {
  const errors = {};

  // Business name
  if (!data.businessName || data.businessName.trim().length < 2) {
    errors.businessName = 'Business name must be at least 2 characters';
  } else if (data.businessName.length > 100) {
    errors.businessName = 'Business name cannot exceed 100 characters';
  }

  // Legal business name (optional)
  if (data.legalBusinessName && data.legalBusinessName.trim().length < 2) {
    errors.legalBusinessName = 'Legal business name must be at least 2 characters';
  }

  // Bio (optional)
  if (data.bio && data.bio.length > 1000) {
    errors.bio = 'Bio cannot exceed 1000 characters';
  }

  // Notification email (optional)
  if (data.notificationEmail && !validateEmail(data.notificationEmail)) {
    errors.notificationEmail = 'Please provide a valid email address';
  }

  // Contact phone (optional)
  if (data.contactPhone && !validatePhone(data.contactPhone)) {
    errors.contactPhone = 'Please provide a valid phone number';
  }

  // Website URL (optional)
  if (data.websiteUrl && !validateUrl(data.websiteUrl)) {
    errors.websiteUrl = 'Please provide a valid website URL';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Check if step is complete
export const isStepComplete = (stepNumber, data) => {
  switch (stepNumber) {
    case 1:
      return validateBusinessInfo(data).isValid;
    default:
      return false;
  }
};

// Get validation errors for a step
export const getStepValidationErrors = (stepNumber, data) => {
  switch (stepNumber) {
    case 1:
      return validateBusinessInfo(data).errors;
    default:
      return {};
  }
};

// Format error messages for display
export const formatErrorMessages = (errors) => {
  return Object.entries(errors).map(([field, message]) => ({
    field,
    message,
  }));
};

// Check overall onboarding completeness
export const checkOnboardingCompleteness = (steps) => {
  if (!steps || steps.length === 0) return 0;

  const completedSteps = steps.filter((step) => step.status === 'completed').length;
  const totalSteps = steps.length;

  return Math.round((completedSteps / totalSteps) * 100);
};

// Get next incomplete step
export const getNextIncompleteStep = (steps) => {
  if (!steps || steps.length === 0) return 1;

  const incompleteStep = steps.find((step) => step.status !== 'completed' && step.status !== 'skipped');
  return incompleteStep ? incompleteStep.step_number : steps.length;
};

// Verification status labels
export const VERIFICATION_STATUS_LABELS = {
  draft: 'Draft',
  pending_documents: 'Pending Documents',
  pending_address_verification: 'Address Verification Pending',
  address_code_sent: 'Verification Code Sent',
  under_review: 'Under Review',
  verified: 'Verified',
  rejected: 'Rejected',
  suspended: 'Suspended',
};

// Get status color
export const getStatusColor = (status) => {
  const colors = {
    draft: 'gray',
    pending_documents: 'yellow',
    pending_address_verification: 'orange',
    address_code_sent: 'blue',
    under_review: 'purple',
    verified: 'green',
    rejected: 'red',
    suspended: 'red',
  };

  return colors[status] || 'gray';
};

export default {
  validateBusinessInfo,
  validateEmail,
  validatePhone,
  validateUrl,
  isStepComplete,
  getStepValidationErrors,
  formatErrorMessages,
  checkOnboardingCompleteness,
  getNextIncompleteStep,
  VERIFICATION_STATUS_LABELS,
  getStatusColor,
};
